<template>
  <div class="modal-backdrop credential-modal-backdrop">
    <div class="modal-container credential-modal">
      <div class="modal-header">
        <div class="credential-cloud-title">
          {{ isWP ? 'Пользователи WordPress' : 'Telegram боты' }}
        </div>
        <button type="button" class="modal-close" @click="$emit('close')" aria-label="Close">
          &times;
        </button>
      </div>

      <div class="modal-body credential-split">
        <section class="credential-panel credential-panel--list">
          <div class="credential-panel__title">Выберите из существующих</div>

          <div v-if="loading" class="text-muted small mt-1">Загрузка...</div>

          <div v-else-if="credentials.length" class="credential-list">
            <label
              v-for="cred in credentials"
              :key="cred.id"
              class="credential-item"
              :class="{ 'credential-item--active': Number(selectedCredential?.id) === Number(cred.id) }"
              :for="'cred_' + cred.id"
            >
              <input
                class="form-check-input credential-item__radio"
                type="radio"
                :id="'cred_' + cred.id"
                :value="cred"
                v-model="selectedCredential"
              />
              <div class="credential-item__content">
                <div class="credential-item__name">{{ cred.label || 'Без метки' }}</div>
              </div>
            </label>
          </div>

          <div v-else class="text-muted small mt-1">
            Пока нет сохранённых {{ isWP ? 'пользователей' : 'ботов' }}.
          </div>
        </section>

        <section class="credential-panel credential-panel--create">
          <div class="credential-panel__title">Добавить нового</div>

          <div class="mb-2">
            <label class="left-headers form-label mb-0">Метка</label>
            <input
              type="text"
              v-model.trim="newCredential.label"
              class="form-control form-control-sm"
              :disabled="adding"
            />
          </div>

          <template v-if="isWP">
            <div class="mb-2">
              <label class="left-headers form-label mb-0">Имя пользователя</label>
              <input
                type="text"
                v-model.trim="newCredential.login"
                class="form-control form-control-sm"
                :disabled="adding"
                placeholder="editor_user"
              />
            </div>
            <div class="mb-2">
              <label class="left-headers form-label mb-0">Пароль</label>
              <input
                type="password"
                v-model.trim="newCredential.password"
                class="form-control form-control-sm"
                :disabled="adding"
                placeholder="••••••••"
              />
            </div>
          </template>

          <template v-else>
            <div class="mb-2">
              <label class="left-headers form-label mb-0">Token</label>
              <input
                type="text"
                v-model.trim="newCredential.token"
                class="form-control form-control-sm"
                :disabled="adding"
                placeholder="Напр.: 1234567890:ABCDEF..."
              />
            </div>

            <div class="mb-2">
              <label class="left-headers form-label mb-0">Bot name</label>
              <input
                type="text"
                v-model.trim="newCredential.botName"
                class="form-control form-control-sm"
                :disabled="adding"
                placeholder="my_bot"
              />
            </div>
          </template>

          <div class="d-flex align-items-center gap-2 mt-2">
            <button
              class="btn btn-outline-primary btn-sm"
              @click="addCredential"
              :disabled="adding || !canAdd"
            >
              {{ adding ? 'Добавление...' : 'Добавить' }}
            </button>
            <span v-if="addError" class="text-danger small">{{ addError }}</span>
          </div>
        </section>
      </div>

      <div class="modal-footer">
        <button class="btn btn-secondary btn-sm" @click="$emit('close')">Закрыть</button>
        <button
          class="btn btn-primary btn-sm"
          @click="submitSelection"
          :disabled="!selectedCredential || savingSelection"
        >
          {{ savingSelection ? 'Сохранение...' : 'Сохранить' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'CredentialModal',
  props: {
    type: { type: String, required: true },
    selected: { type: [Object, Number, String], default: null },
  },
  data() {
    return {
      loading: true,
      adding: false,
      savingSelection: false,
      addError: '',
      credentials: [],
      selectedCredential: null,
      newCredential: {
        label: '',
        token: '',
        botName: '',
        login: '',
        password: '',
      },
    }
  },
  computed: {
    isWP() {
      return String(this.type).toUpperCase() === 'WORDPRESS'
    },
    canAdd() {
      if (this.isWP) {
        return this.newCredential.label && this.newCredential.login && this.newCredential.password
      }
      return this.newCredential.label && this.newCredential.token && this.newCredential.botName
    },
  },
  async mounted() {
    await this.fetchCredentials()
    this.applyPreselect()
  },
  watch: {
    credentials() {
      this.applyPreselect()
    },
    selected() {
      this.applyPreselect(true)
    },
  },
  methods: {
    async fetchCredentials() {
      this.loading = true
      try {
        const { data } = await axios.get(`/api/credentials/by-type/${this.type}`)
        this.credentials = data
      } catch (e) {
        console.error('Ошибка при загрузке credentials:', e)
      } finally {
        this.loading = false
      }
    },
    applyPreselect(force = false) {
      if (!this.selected) return
      if (this.selectedCredential && !force) return

      const selectedId =
        typeof this.selected === 'object' && this.selected ? this.selected.id : Number(this.selected)

      if (!selectedId) return

      const found = this.credentials.find(c => Number(c.id) === Number(selectedId))
      if (found) {
        this.selectedCredential = found
        return
      }

      if (typeof this.selected === 'object' && this.selected.id) {
        this.credentials.unshift(this.selected)
        this.selectedCredential = this.selected
      }
    },
    async addCredential() {
      if (!this.canAdd || this.adding) return
      this.addError = ''
      this.adding = true
      try {
        const payload = {
          type: this.type,
          label: this.newCredential.label,
        }
        if (this.isWP) {
          payload.login = this.newCredential.login
          payload.password = this.newCredential.password
        } else {
          payload.token = this.newCredential.token
          payload.botName = this.newCredential.botName
        }

        const { data } = await axios.post('/api/credentials', payload)
        this.credentials.unshift(data)
        this.selectedCredential = data

        this.newCredential = {
          label: '',
          token: '',
          botName: '',
          login: '',
          password: '',
        }
      } catch (e) {
        console.error('Ошибка при сохранении credentials:', e)
        this.addError = 'Не удалось создать. Проверьте данные и попробуйте снова.'
      } finally {
        this.adding = false
      }
    },
    async submitSelection() {
      if (!this.selectedCredential || this.savingSelection) return
      this.savingSelection = true
      try {
        this.$emit('select', this.selectedCredential)
        this.addError = ''
      } finally {
        this.savingSelection = false
      }
    },
  },
}
</script>

<style scoped>
.credential-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 10px;
}

.credential-modal {
  width: min(96vw, 860px);
  max-height: min(94vh, 860px);
  background: #fff;
  border: 1px solid #dfe7f5;
  border-radius: 16px;
  box-shadow: 0 24px 52px rgba(15, 23, 42, 0.24);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  position: relative;
  display: flex;
  justify-content: flex-end;
  padding: 10px 12px 0;
  min-height: 44px;
}

.credential-cloud-title {
  position: absolute;
  left: 14px;
  top: 10px;
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: linear-gradient(180deg, #f2f7ff 0%, #eaf2ff 100%);
  color: #2f4f88;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: 0 4px 10px rgba(49, 94, 227, 0.08);
}

.modal-close {
  width: 34px;
  height: 34px;
  border: 1px solid #dce6f6;
  border-radius: 10px;
  background: #fff;
  color: #6c7a95;
  font-size: 24px;
  line-height: 1;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.modal-close:hover {
  background: #f3f6fd;
  color: #2e3d59;
  transform: scale(1.02);
}

.modal-close:focus {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.2);
}

.credential-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 0 12px 12px;
  overflow: hidden;
  min-height: 0;
}

.credential-panel {
  border: 1px solid #e1eaf8;
  border-radius: 12px;
  background: #f8fbff;
  padding: 12px;
  min-height: 0;
  overflow-y: auto;
}

.credential-panel--create {
  background: #fbfdff;
}

.credential-panel::-webkit-scrollbar {
  width: 8px;
}

.credential-panel::-webkit-scrollbar-thumb {
  border-radius: 8px;
  background: #d1ddef;
}

.credential-panel__title {
  display: inline-flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 5px 9px;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: #eff5ff;
  color: #3b5690;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.credential-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.credential-item {
  border: 1px solid #dfe8f8;
  border-radius: 10px;
  background: #fff;
  padding: 8px;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px;
  align-items: start;
  cursor: pointer;
}

.credential-item--active {
  border-color: #9cbaf6;
  box-shadow: inset 0 0 0 1px rgba(13, 110, 253, 0.14);
  background: #f3f8ff;
}

.credential-item__radio {
  margin-top: 2px;
}

.credential-item__name {
  color: #26344f;
  font-weight: 700;
  font-size: 13px;
  line-height: 1.25;
}

.credential-item__meta {
  color: #667998;
  font-size: 12px;
  margin-top: 2px;
  line-height: 1.25;
}

.left-headers {
  padding-left: 1px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #4d5f82;
  text-transform: uppercase;
}

.credential-panel .form-control,
.credential-panel .form-select {
  border-radius: 10px;
}

.credential-panel .form-control:focus,
.credential-panel .form-select:focus {
  border-color: #95b8ff;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.14);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 12px 12px;
  border-top: 1px solid #e7edf8;
  background: #fff;
}

.modal-footer .btn {
  min-width: 90px;
  padding: 0.3rem 0.65rem;
  font-size: 0.84rem;
}

@media (max-width: 920px) {
  .credential-split {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .credential-modal-backdrop {
    padding: 8px;
  }

  .credential-modal {
    width: 100%;
    max-height: 96vh;
    border-radius: 12px;
  }

  .credential-split {
    padding: 0 10px 10px;
    gap: 10px;
  }

  .credential-panel {
    padding: 10px;
  }
}
</style>
